import { useRef, useState } from 'react';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { uploadImage } from '../../api/endpoints';
import { assetUrl } from '../../api/client';

interface ImageUploaderProps {
  value: string | null;
  onChange: (url: string | null) => void;
}

export default function ImageUploader({ value, onChange }: ImageUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const result = await uploadImage(file);
      onChange(result.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : '上传失败');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="image-uploader">
      {value ? (
        <img className="image-uploader-preview" src={assetUrl(value) ?? ''} alt="封面预览" />
      ) : (
        <span className="image-uploader-placeholder" aria-hidden="true">
          {uploading ? <Loader2 className="spinner-sm" /> : <ImagePlus size={20} />}
        </span>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? '上传中…' : value ? '更换图片' : '上传图片'}
          </button>
          {value && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => onChange(null)}
              aria-label="移除图片"
            >
              <X size={15} />
            </button>
          )}
        </div>
        <span className="form-hint">支持 JPG、PNG、WEBP、GIF，大小不超过 2MB</span>
        {error && <span className="form-error">{error}</span>}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        style={{ display: 'none' }}
        onChange={(event) => void handleFile(event.target.files?.[0])}
      />
    </div>
  );
}
